import { isEmail } from '@pperform/pp-utils/validation';
import { useState, useCallback, useMemo } from 'preact/hooks';
import { useTranslation } from 'react-i18next';
import {
  getShipmentParams,
  getSearchFormConfigs,
} from 'services/globalConfig.services';
import validateSearchFields from 'utils/validateSearchFields.utils';
import { usePTPConfigContext } from 'GlobalConfig.context';
import { useSearchParams } from 'hooks/useSearchParams';
import type {
  SearchField,
  AdditionalValidation,
  SearchFormFieldConfig,
} from 'interfaces/WidgetConfigs.interface';

interface FormField {
  label: string;
  name: string;
  value: string;
  error: string;
  tooltip?: string;
  placeholder?: string;
}

interface ValidationField {
  key: string;
  name: string;
}

// NOTE: To support additional validation configured with postal_code AND email. Eg: to_address.postal_code|to_address.email
// Modify the field name to display both postal code and email in the label
// https://www.pivotaltracker.com/story/show/187896209
const processValidationField = (
  additionalValidation: AdditionalValidation,
  searchFields: SearchField[],
  t: (key: string) => string,
): ValidationField => {
  const keys = additionalValidation.key.split('|');
  const fieldNames = keys
    .map((key) => {
      const field = searchFields.find((item) => key === item.key);
      return field ? t(field.name) : '';
    })
    .filter(Boolean)
    .join(` ${t('OR')} `);

  return {
    key: additionalValidation.key,
    name: fieldNames,
  };
};

// Helper: Check if a field has validation configured
const hasFieldValidation = (
  fieldKey: string,
  additionalValidation: AdditionalValidation,
): boolean => {
  return additionalValidation.key.split('|').includes(fieldKey);
};

// Helper: Build a form field with proper validation handling
const buildFormField = (
  field: SearchField,
  value: string,
  additionalValidation: AdditionalValidation,
  searchFields: SearchField[],
  t: (key: string) => string,
  searchFormFieldConfig?: SearchFormFieldConfig,
): FormField => {
  if (hasFieldValidation(field.key, additionalValidation)) {
    const validationField = processValidationField(
      additionalValidation,
      searchFields,
      t,
    );
    return {
      label: validationField.name,
      name: validationField.key,
      value,
      error: '',
      tooltip: searchFormFieldConfig?.[validationField.key]?.tooltip,
      placeholder: searchFormFieldConfig?.[validationField.key]?.placeholder,
    };
  }

  return {
    label: t(field.name),
    name: field.key,
    value,
    error: '',
    tooltip: searchFormFieldConfig?.[field.key]?.tooltip,
    placeholder: searchFormFieldConfig?.[field.key]?.placeholder,
  };
};

// Helper: Check if a field should be included based on validation requirements
const shouldIncludeField = (
  fieldKey: string,
  additionalValidation: AdditionalValidation,
  existingFields: FormField[],
): boolean => {
  // If field doesn't require validation, include it
  if (!hasFieldValidation(fieldKey, additionalValidation)) {
    return true;
  }

  // If field requires validation, check if validation prerequisites are met
  const { fields: validationFields } = additionalValidation;
  return validationFields.some((item) =>
    existingFields.some((e) => e.name === item),
  );
};

const useSearchForm = () => {
  const { t } = useTranslation();
  const { updateSearchParams } = useSearchParams();
  const configs = usePTPConfigContext();

  const [formFields, setFormFields] = useState<FormField[]>([]);

  const {
    searchFields,
    additionalValidation,
    searchForm: searchFormConfig,
  } = useMemo(() => getSearchFormConfigs(configs), [configs]);

  const searchFormFieldConfig = searchFormConfig?.searchFormField;

  const initRetrySearchForm = useCallback(() => {
    if (searchFields.length === 0) {
      updateSearchParams({ params: {}, partial: false });
      return;
    }

    // Get fresh params from URL on each call
    const params = getShipmentParams(configs);

    const formFields = params.reduce<FormField[]>((acc, { key, value }) => {
      const field = searchFields.find((e) =>
        [e?.urlParam, e?.tagParam].includes(key),
      );

      if (!field) return acc;

      // Skip if validation prerequisites are not met
      if (!shouldIncludeField(field.key, additionalValidation, acc)) {
        return acc;
      }

      return [
        ...acc,
        buildFormField(
          field,
          value,
          additionalValidation,
          searchFields,
          t,
          searchFormFieldConfig,
        ),
      ];
    }, []);

    setFormFields(formFields);
  }, [
    searchFields,
    additionalValidation,
    searchFormFieldConfig,
    configs,
    t,
    updateSearchParams,
  ]);

  const initSearchForm = useCallback(
    (key: string) => {
      const field = searchFields.find((item) => item.key === key);

      if (!field) return;

      // Get fresh params from URL on each call
      const params = getShipmentParams(configs);

      const formFields: FormField[] = [];

      // Add primary field
      const primaryFieldValue =
        params.find((param) =>
          [field.urlParam, field.tagParam].includes(param.key),
        )?.value || '';

      formFields.push({
        name: field.key,
        label: t(field.name),
        value: primaryFieldValue,
        error: '',
        tooltip: searchFormFieldConfig?.[field.key]?.tooltip,
        placeholder: searchFormFieldConfig?.[field.key]?.placeholder,
      });

      // Add additional validation field if configured
      const hasValidation =
        additionalValidation.key &&
        additionalValidation.fields?.includes(field.key);

      if (hasValidation) {
        const additionalField = processValidationField(
          additionalValidation,
          searchFields,
          t,
        );
        const additionalFieldValue =
          params.find((param) => {
            const foundField = searchFields.find(
              (item) => item.key === additionalField.key,
            );
            return (
              foundField &&
              [foundField.urlParam, foundField.tagParam].includes(param.key)
            );
          })?.value || '';

        formFields.push({
          name: additionalField.key,
          label: additionalField.name,
          value: additionalFieldValue,
          error: '',
          tooltip: searchFormFieldConfig?.[additionalField.key]?.tooltip,
          placeholder:
            searchFormFieldConfig?.[additionalField.key]?.placeholder,
        });
      }

      setFormFields(formFields);
    },
    [searchFields, additionalValidation, searchFormFieldConfig, configs, t],
  );

  const onChangeSearchForm = (event: Event) => {
    if (event.target instanceof HTMLInputElement) {
      const { name, value } = event.target;
      const error = validateSearchFields(name, value);

      const newSearchFields = formFields.map((item) =>
        item.name === name ? { ...item, error, value } : item,
      );

      setFormFields(newSearchFields);
    }
  };

  const onResetSearchForm = () => {
    const newSearchFields = formFields.map((item) => ({
      ...item,
      value: '',
      error: '',
    }));

    setFormFields(newSearchFields);
  };

  const onSubmitSearchForm =
    (onSubmit: (values: Record<string, string | number>) => void) =>
    (event: Event) => {
      event.preventDefault();

      let isInvalid = false;

      const newSearchFields = formFields.map((item) => {
        const error = validateSearchFields(item.name, item.value);

        if (!error) {
          return {
            ...item,
            // only trim value when no error found to avoid confusing the user
            value:
              typeof item.value === 'string' ? item.value.trim() : item.value,
          };
        }

        isInvalid = true;
        return { ...item, error: t(error) };
      });

      if (isInvalid) {
        setFormFields(newSearchFields);
        return;
      }

      // Uses already-validated and trimmed values from newSearchFields
      const searchTerms = newSearchFields.reduce<Record<string, string>>(
        (acc, { name, value }) => {
          // NOTE: To support addtional validation is configured with posstal_code AND email. Eg: to_address.postal_code|to_address.email
          // We need to check if the value matches the postal code or email to decide which field to add
          // https://www.pivotaltracker.com/story/show/187896209
          const keys = name.split('|');
          let fieldName = name;

          if (keys.length > 1) {
            fieldName = isEmail(value) ? keys[1] : keys[0];
          }

          acc[fieldName] = value;
          return acc;
        },
        {},
      );

      if (onSubmit) {
        onSubmit(searchTerms);
      }
    };

  /**
   * Perform search by updating URL parameters with search terms
   */
  const onSearchBySearchTerms = useCallback(
    (searchTerms: Record<string, string>) => {
      const queryParams = Object.entries(searchTerms).reduce(
        (result, [key, value]) => {
          const field = searchFields.find((item) => item.key === key);
          if (field?.urlParam) {
            return {
              ...result,
              [field.urlParam]: value,
            };
          }
          return result;
        },
        {} as Record<string, string>,
      );

      const paramsToRemove = searchFields.map((item) => item.urlParam);

      updateSearchParams({
        params: queryParams,
        keysToRemove: paramsToRemove,
      });
    },
    [searchFields, updateSearchParams],
  );

  return {
    formFields,
    initSearchForm,
    initRetrySearchForm,
    onChangeSearchForm,
    onResetSearchForm,
    onSubmitSearchForm,
    onSearchBySearchTerms,
  };
};

export default useSearchForm;
